/**
 * useProctoring - Continuous face verification for assessment integrity
 *
 * SECURITY MODEL:
 * - Verifies identity at configurable intervals (default 2 seconds)
 * - LENIENT on face identity (low threshold, high tolerance for mismatches)
 * - STRICT on face visibility (warns if face cannot be seen)
 * - STRICT on multiple faces (warns if more than one person detected)
 * - Tab-switch / fullscreen-exit checks only run when the assessment enables
 *   `browserLockdown` (per-assessment proctoring config, default on)
 */

import { useEffect, useRef } from 'react'
import { useAssessmentStore } from '../store/assessmentStore'
import { verifyFaceFromBase64 } from '../services/faceDetection'
import { useWebcamCapture } from '../components/Webcam'
import { getProctoringConfig } from '../store/proctoringConfigStore'
import { isFrameOccluded } from '../utils/frameAnalysis'
import { isFullscreenActive, isFullscreenSupported } from '../utils/fullscreen'

interface UseProctoringOptions {
  onWarning: (message: string) => void
  onResolved: () => void
  /** Called when the user returns to fullscreen (no face-related speech) */
  onFullscreenRestored?: () => void
  /** Called when the user leaves fullscreen (lockdown only) */
  onFullscreenExited?: () => void
  onSuspend?: (reason: 'multiple_faces' | 'identity_verification_failed') => void
}

export function useProctoring({
  onWarning,
  onResolved,
  onFullscreenRestored,
  onFullscreenExited,
  onSuspend,
}: UseProctoringOptions) {
  const {
    faceEncodingId,
    sessionToken,
    proctoringStatus,
    updateProctoringStatus,
    addProctoringEvent,
  } = useAssessmentStore()

  const { capture } = useWebcamCapture()
  const browserLockdown = getProctoringConfig().browserLockdown

  // Keep latest callbacks/config in refs so the verification interval is stable
  // (inline callbacks / new config objects must NOT restart the loop every render)
  const onWarningRef = useRef(onWarning)
  const onResolvedRef = useRef(onResolved)
  const onFullscreenRestoredRef = useRef(onFullscreenRestored)
  const onFullscreenExitedRef = useRef(onFullscreenExited)
  const onSuspendRef = useRef(onSuspend)
  const captureRef = useRef(capture)
  const faceEncodingIdRef = useRef(faceEncodingId)
  const sessionTokenRef = useRef(sessionToken)

  onWarningRef.current = onWarning
  onResolvedRef.current = onResolved
  onFullscreenRestoredRef.current = onFullscreenRestored
  onFullscreenExitedRef.current = onFullscreenExited
  onSuspendRef.current = onSuspend
  captureRef.current = capture
  faceEncodingIdRef.current = faceEncodingId
  sessionTokenRef.current = sessionToken

  const hasActiveWarning = useRef(false)
  const verificationInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastFaceDetectedTime = useRef<number>(Date.now())
  const lastNoFaceViolationTime = useRef<number>(0)
  const consecutiveIdentityFailures = useRef<number>(0)
  const isSuspended = useRef(false)
  const inFlight = useRef(false)
  const webcamReadyAt = useRef<number | null>(null)

  useEffect(() => {
    let isActive = true
    const config = getProctoringConfig()
    const intervalMs = Math.max(1000, config.verificationIntervalMs || 2000)
    const noFaceThresholdMs = config.noFaceWarningThresholdMs || 2000

    const updateStatusIfChanged = (updates: { faceDetected?: boolean; identityMatch?: number }) => {
      const current = useAssessmentStore.getState().proctoringStatus
      const nextFace = updates.faceDetected ?? current.faceDetected
      const nextMatch = updates.identityMatch ?? current.identityMatch
      if (nextFace === current.faceDetected && nextMatch === current.identityMatch) {
        return
      }
      updateProctoringStatus(updates)
    }

    const showWarningOnce = (message: string) => {
      if (!hasActiveWarning.current) {
        hasActiveWarning.current = true
        onWarningRef.current(message)
      }
    }

    const handleNoFaceDetected = (now: number, immediate: boolean) => {
      // Give the remounted webcam a few seconds before treating empty frames as violations
      if (webcamReadyAt.current === null) {
        return
      }
      if (now - webcamReadyAt.current < 3000 && !immediate) {
        return
      }

      updateStatusIfChanged({ faceDetected: false, identityMatch: 0 })

      const pastThreshold =
        immediate || now - lastFaceDetectedTime.current > noFaceThresholdMs

      if (pastThreshold) {
        const violationCooldownMs = Math.max(intervalMs * 2, 4000)
        if (now - lastNoFaceViolationTime.current >= violationCooldownMs) {
          lastNoFaceViolationTime.current = now
          addProctoringEvent({
            type: 'no_face',
            timestamp: now,
            details: immediate ? 'Camera blocked or unavailable' : 'No face detected',
          })
        }
        showWarningOnce("I can't detect your face. Please uncover your camera and stay visible.")
      }
    }

    const handleEnrollmentMissing = (facesDetected: number, now: number) => {
      if (facesDetected > 0) {
        lastFaceDetectedTime.current = now
      }
      updateStatusIfChanged({ faceDetected: facesDetected > 0, identityMatch: 0 })
    }

    const handleMultipleFaces = (facesDetected: number, now: number) => {
      lastFaceDetectedTime.current = now
      addProctoringEvent({
        type: 'multiple_faces',
        timestamp: now,
        details: `${facesDetected} faces detected`,
      })
      updateStatusIfChanged({ faceDetected: true, identityMatch: 0 })
      showWarningOnce(
        `I can see ${facesDetected} people in the camera. Please ensure only you are visible during the assessment.`
      )
    }

    const handleSingleFaceDetected = (similarity: number, now: number) => {
      lastFaceDetectedTime.current = now
      const encodingId = faceEncodingIdRef.current
      const threshold = config.identitySimilarityThreshold
      const matchScore = similarity || (encodingId ? 50 : 100)
      const effectiveMatch =
        encodingId && similarity < threshold ? Math.max(0, similarity) : matchScore

      updateStatusIfChanged({ faceDetected: true, identityMatch: effectiveMatch })
      consecutiveIdentityFailures.current = 0

      if (hasActiveWarning.current) {
        hasActiveWarning.current = false
        onResolvedRef.current()
      }
    }

    const runVerificationCycle = async () => {
      if (!isActive || isSuspended.current || inFlight.current) return
      inFlight.current = true

      try {
        const now = Date.now()
        const imageBase64 = captureRef.current()

        if (!imageBase64) {
          // Webcam not ready yet — wait, don't thrash state
          return
        }

        if (webcamReadyAt.current === null) {
          webcamReadyAt.current = now
        }

        let occluded = false
        try {
          occluded = await isFrameOccluded(imageBase64)
        } catch {
          occluded = false
        }

        if (occluded) {
          handleNoFaceDetected(Date.now(), true)
          return
        }

        const result = await verifyFaceFromBase64(
          imageBase64,
          faceEncodingIdRef.current || undefined,
          sessionTokenRef.current || undefined
        )

        if (!isActive) return

        const resultNow = Date.now()
        console.log('[Proctoring] Verification result:', {
          verified: result.verified,
          similarity: result.similarity,
          facesDetected: result.facesDetected,
          error: result.error,
        })

        if (result.error === 'no_enrolled_face' || result.error === 'encoding_expired') {
          handleEnrollmentMissing(result.facesDetected, resultNow)
        } else if (result.facesDetected === 0 || result.error === 'no_face_detected') {
          handleNoFaceDetected(resultNow, false)
        } else if (result.facesDetected > 1) {
          handleMultipleFaces(result.facesDetected, resultNow)
        } else {
          handleSingleFaceDetected(result.similarity || 0, resultNow)
        }
      } catch (error) {
        console.error('[Proctoring] Verification error:', error)
        if (webcamReadyAt.current !== null) {
          handleNoFaceDetected(Date.now(), true)
        }
      } finally {
        inFlight.current = false
      }
    }

    // Delay first cycle slightly so the assessment webcam can mount
    const startTimer = setTimeout(() => {
      void runVerificationCycle()
      verificationInterval.current = setInterval(() => {
        void runVerificationCycle()
      }, intervalMs)
    }, 1500)

    return () => {
      isActive = false
      clearTimeout(startTimer)
      if (verificationInterval.current) {
        clearInterval(verificationInterval.current)
        verificationInterval.current = null
      }
    }
    // Intentionally narrow deps — callbacks/config live in refs to avoid restart loops
  }, [faceEncodingId, sessionToken, addProctoringEvent, updateProctoringStatus])

  useEffect(() => {
    if (!browserLockdown) return

    const handleVisibilityChange = () => {
      if (document.hidden) {
        addProctoringEvent({ type: 'tab_switch', timestamp: Date.now(), details: 'Tab switched' })
        onWarningRef.current('Please stay on this page during the assessment.')
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [addProctoringEvent, browserLockdown])

  useEffect(() => {
    if (!browserLockdown) return
    if (!isFullscreenSupported()) return

    // Debounce only duplicate browser events (~same exit), not legitimate re-exits
    let lastExitWarnAt = 0
    const EXIT_DEBOUNCE_MS = 400
    let wasFullscreen = isFullscreenActive()

    const handleFullscreenChange = () => {
      const nowFullscreen = isFullscreenActive()

      if (wasFullscreen && !nowFullscreen) {
        // Exited fullscreen
        addProctoringEvent({
          type: 'fullscreen_exit',
          timestamp: Date.now(),
          details: 'Exited fullscreen mode',
        })
        onFullscreenExitedRef.current?.()
        const now = Date.now()
        if (now - lastExitWarnAt >= EXIT_DEBOUNCE_MS) {
          lastExitWarnAt = now
          onWarningRef.current(
            'Please return to fullscreen mode. Use the Fullscreen button or tap Enter Fullscreen to continue.'
          )
        }
      } else if (!wasFullscreen && nowFullscreen) {
        // Returned to fullscreen — clear warning so the next exit is recognized
        onFullscreenRestoredRef.current?.()
      }

      wasFullscreen = nowFullscreen
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [addProctoringEvent, browserLockdown])

  return {
    proctoringStatus,
    hadWarning: hasActiveWarning.current,
    isSuspended: isSuspended.current,
    consecutiveFailures: consecutiveIdentityFailures.current,
  }
}
